<template>
    <div
        class="q-pb-lg"
        :class="{
            'desktop-layout-form-view': $q.platform.is.desktop
        }"
    >
        <q-form @submit="isEdit ? update() : create()" ref="eventForm">
            <q-input
                v-model="form.name"
                @clear="form.name = ''"
                :rules="[val => val !== '' || $t('Enter a name')]"
                :label="$t('Name')"
                class="q-mb-xs"
                bg-color="white"
                clearable
                lazy-rules
                outlined
                color="accent"
            ></q-input>

            <q-table
                :filter="table.search"
                :filter-method="filterTable"
                :rows="assets"
                :columns="table.cols"
                :rows-per-page-options="[30]"
                selection="multiple"
                v-model:selected="form.selectedAssets"
                row-key="id"
                class="q-mb-lg"
                binary-state-sort
                :no-data-label="$t('No data available')"
            >
                <!-- top bar -->
                <template v-slot:top>
                    <div class="full-width">
                        <!-- title -->
                        <div class="col-12 text-h4 q-mb-md">
                            {{ $t('Assets') }}
                        </div>
                        <!-- search bar -->
                        <div
                            class="row fit"
                            :class="
                                $q.screen.lt.sm
                                    ? ''
                                    : 'wrap justify-between items-start content-start'
                            "
                        >
                            <q-input
                                v-model="table.search"
                                @clear="table.search = ''"
                                :placeholder="$t('Search asset')"
                                :class="
                                    $q.screen.lt.sm
                                        ? 'col-12 q-mb-md'
                                        : 'col q-mr-md'
                                "
                                debounce="10"
                                clearable
                                outlined
                                dense
                                color="accent"
                            >
                                <template v-slot:prepend>
                                    <q-icon name="search" />
                                </template>
                            </q-input>
                        </div>
                    </div>
                </template>
            </q-table>
            <div class="form__actions">
                <div>
                    <q-btn
                        v-if="isEdit"
                        @click="handleRemove"
                        :color="
                            $userCannot('edit', 'groups') ? 'grey' : 'negative'
                        "
                        :disable="$userCannot('edit', 'groups')"
                        class="float-left q-px-xs bg-white"
                        icon="fas fa-trash-alt"
                        outline
                    ></q-btn>
                    <q-tooltip v-if="$userCannot('edit', 'groups')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <div>
                    <q-btn
                        :label="$t('Save')"
                        :color="
                            $userCannot('edit', 'groups') ? 'grey' : 'primary'
                        "
                        :disable="$userCannot('edit', 'groups')"
                        type="submit"
                        class="float-right"
                    />
                    <q-tooltip v-if="$userCannot('edit', 'groups')">
                        {{
                            $t(
                                'You do not have permission to perform this action.'
                            )
                        }}
                    </q-tooltip>
                </div>
                <q-btn
                    @click="$router.go(-1)"
                    :label="$t('Cancel')"
                    class="float-right q-mr-sm bg-white"
                    text-color="primary"
                    outline
                />
            </div>
        </q-form>
    </div>
</template>

<script>
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import UpdateURL from '@mixins/UpdateURL.vue';
import { mapGetters } from 'vuex';
import { useSharedStore } from '@stores/shared';
import { mapActions } from 'pinia';

export default {
    name: 'GroupDetail',
    mixins: [BreadcrumbsManager, UpdateURL],
    props: {
        groupId: {
            type: [Number, String],
            required: false,
            default: null
        },
        facilityId: {
            type: [Number, String],
            required: false
        },
        name: {
            type: String,
            required: false,
            default: ''
        },
        selectedAssets: {
            type: Array,
            required: false,
            default: () => []
        }
    },
    async mounted() {
        // init form
        if (this.isEdit) {
            this.initForm();
        }

        // set breadcrumbs
        this.setBreadcrumbs([
            {
                label: this.$t('Facilities'),
                route: {
                    name: 'facility_list'
                }
            },
            {
                label: this.facility.name,
                route: {
                    name: 'facility_dashboard',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Settings'),
                route: {
                    name: 'facility_settings',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.$t('Groups'),
                route: {
                    name: 'group_list',
                    params: {
                        facilityId: this.facility.id
                    }
                }
            },
            {
                label: this.isEdit ? this.form.name : this.$t('Create Group')
            }
        ]);
    },
    data() {
        return {
            baseURL: 'assetgroups/',
            url: 'assets/',
            form: {
                name: '',
                selectedAssets: []
            },
            table: {
                cols: [
                    {
                        align: 'left',
                        field: 'name',
                        name: 'name',
                        label: this.$t('Name'),
                        sortable: true
                    },
                    {
                        align: 'left',
                        field: 'description',
                        name: 'description',
                        label: this.$t('Description')
                    }
                ],
                pagination: {
                    sortBy: '',
                    descending: false,
                    page: 1,
                    rowsPerPage: 30,
                    rowsNumber: null
                },
                search: ''
            }
        };
    },
    computed: {
        ...mapGetters({
            getAssets: 'facilities/getAssets',
            getFacility: 'facilities/getFacility',
            getFacilities: 'facilities/getFacilities'
        }),
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facility() {
            return this.$store.getters['facilities/getFacility'](
                this.facilityId
            );
        },
        /**
         * Get group from store
         * @return {Object} - Group.
         */
        group() {
            return this.$store.getters['facilities/getGroup'](this.groupId);
        },
        /**
         * Determine if form is in edit mode.
         * @return {Boolean}
         */
        isEdit() {
            return this.groupId !== null;
        },
        /**
         * Get selected facility.
         * @return {Object} - Facility.
         */
        facilities() {
            return this.$store.getters['facilities/getFacilities'];
        },
        /**
         * Get Assets for facility.
         * @return {Object} - Facility.
         */
        assets() {
            return this.$store.getters['facilities/getAssets'](this.facilityId);
        },
        payload() {
            return {
                name: this.form.name,
                facility: this.facilityId,
                assets: this.form.selectedAssets.map(item => item.id)
            };
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        /**
         * Initialize form.
         */
        initForm() {
            this.form['name'] = this.group.name;

            let selectedAssets = [];
            this.group.assets.forEach(assetId => {
                const asset =
                    this.$store.getters['facilities/getAsset'](assetId);
                if (asset) {
                    selectedAssets.push(asset);
                }
            });
            this.form['selectedAssets'] = selectedAssets;
        },
        /**
         * Allow the user to search through the list of assets
         */
        filterTable(rows) {
            return rows.filter(asset =>
                asset.name
                    .toLowerCase()
                    .includes(this.table.search.toLowerCase())
            );
        },
        /**
         * Create Group.
         */
        async create() {
            this.beginLoading(`${this.$t('Creating group')}...`);
            await this.$axios
                .post(this.baseURL, this.payload)
                .then(response => {
                    this.$store.dispatch(
                        'facilities/addAssetGroup',
                        response.data
                    );
                    this.handleSuccess(this.$t('Group created'));
                })
                .catch(error => {
                    this.handleApiErrors(error, 'create');
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Update group.
         */
        async update() {
            this.beginLoading(`${this.$t('Updating group')}...`);
            await this.$axios
                .put(`${this.baseURL}${this.groupId}/`, this.payload)
                .then(response => {
                    this.$store.dispatch(
                        'facilities/updateAssetGroup',
                        response.data
                    );
                    this.handleSuccess(this.$t('Group updated'));
                })
                .catch(error => {
                    this.handleApiErrors(error, 'update');
                })
                .finally(() => {
                    this.stopLoading();
                });
        },
        /**
         * Handle API errors based on the action being performed.
         * @param {Object} e - The error object returned from the API.
         * @param {String} action - The action that was being performed when the error occurred.
         */
        handleApiErrors(e, action) {
            const error =
                e?.response?.data && typeof e.response.data === 'object'
                    ? e.response.data
                    : {};
            if (
                error['non_field_errors'] &&
                Array.isArray(error['non_field_errors']) &&
                error['non_field_errors'].includes(
                    'The fields facility, name must make a unique set.'
                )
            ) {
                this.$notifyError(
                    this.$t(
                        'Group name already exists. Please choose a different name.'
                    ),
                    error
                );
            } else {
                if (action === 'create') {
                    this.$notifyError(
                        this.$t(
                            'Failed to create group. Please try again later.'
                        ),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to update group. Please try again later.'
                        ),
                        error
                    );
                }
            }
        },
        /**
         * Handle remove group.
         */
        async handleRemove() {
            this.$confirmDelete(
                this.$t('Are you sure you want to delete this group?')
            ).onOk(async () => {
                await this.remove();
            });
        },
        /**
         * Remove group.
         */
        async remove() {
            await this.$axios
                .delete(`${this.baseURL}${this.groupId}/`)
                .then(response => {
                    this.$store.dispatch(
                        'facilities/removeAssetGroup',
                        this.groupId
                    );
                    this.handleSuccess(this.$t('Group deleted'));
                })
                .catch(error => {
                    this.$notifyError(
                        this.$t(
                            'Failed to remove Group. Please try again later.'
                        ),
                        error
                    );
                });
        },
        /**
         * Handle successful Create/Update/Remove of group.
         * Redirect to list of groups and reload the list of assets to
         * make sure that the list of groups stored in the Vuex store is updated
         */
        handleSuccess(message) {
            this.$notifySuccess(message);
            this.$router.push({ name: 'group_list' });

            // reload the list of assets to
            // make sure that the list of groups stored in the Vuex store is updated
            // this.$store.dispatch('facilities/loadAssets', this.facilityId);
        }
    }
};
</script>

<style lang="scss" scoped></style>
